/**
 * The SQL of the company's spend report (partner desk) and of the fleet's reports (fleet console) — D534, phase E of the analytics program
 * (research doc 05 section 3.6 "Frota" rows 49-55 and section 3.8 "Empresa" rows 67-74). Pure: parameterized text and the row each statement returns; the
 * API runs it in ONE read-only transaction as the least-privilege reader and folds the rows into the wire.
 *
 * TWO AUDIENCES, TWO TENANTS, BOTH BUILT BY THE SERVER:
 *
 *  - COMPANY (the desk, owner | manager): a {@link PartnerAccountTenant} (the account the durable membership authorized and its operating markets).
 *    These are the account's OWN transactions - one company reading its own orders and charges, not an aggregate over other people's records - so, like E-049
 *    (D527), they are exempt from the partner cohort floor; they keep the per-tenant limiter and an audit row on export. The cost centre of an order is the
 *    one its CHARGE carries (`partner_charges.cost_center_id` -> `fact_money.partner_account_key` -> `dim_partner_account.cost_center_code`): an order that booked no
 *    charge (a guest paying cash) has none and reads as "no cost centre", never as a guess.
 *  - FLEET (the fleet console, `fleet:view` on a FLEET scope): a {@link FleetTenant}: the fleet organization, the markets it may operate in and the drivers
 *    bound to it by an ACTIVE role grant, each with the instant the grant began (`since`). The membership is the main database's (`fleet_role_grants`), read by
 *    the API; the request never names a driver, a market or an organization that the server has not authorized. A trip, an offer or a rating counts for a fleet
 *    only from the instant the driver was attached to it (`requested_at >= since`): a fleet never reads what a driver did before joining it.
 *
 * WHAT IS NOT IN THE FLEET SQL ON PURPOSE: `fact_presence_hour` (one row is one driver's working hours: personal-role only, D530 review P1-1), the driver's name,
 * phone, plate or model (personal columns), the rider, any address or coordinate, any money. Pseudonymous keys never leave the statements: the driver is the
 * `natural_key` the fleet itself attached (the id on its own grant), carried as `driver_ref`.
 */
import { SEMANTIC_VOCABULARY } from './catalog.js';
import { PARTNER_ACCOUNT_JOIN, PARTNER_CHARGE_KINDS, partnerRequestScope } from './orphan-reports.js';
const V = SEMANTIC_VOCABULARY;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const MARKET_ID = /^[a-z]{2}:[a-z0-9]+(?:-[a-z0-9]+)*$/;
const REF = /^[\x21-\x7e]{1,128}$/;
const CENTER_CODE = /^[A-Z0-9][A-Z0-9_.-]{0,31}$/;
const date = (v) => {
    if (!DATE.test(v))
        throw new RangeError('a report date is YYYY-MM-DD');
    return v;
};
const nullable = (values) => (values === null || values === undefined || values.length === 0 ? null : [...values]);
const clampInt = (v, lo, hi) => (Number.isFinite(v) ? Math.min(hi, Math.max(lo, Math.trunc(v))) : lo);
export const B2B_FLEET_LIMITS = {
    /** Rows of a page / of a synchronous CSV export. */
    maxPage: 500,
    defaultPage: 100,
    exportRows: 20_000,
    maxOffset: 20_000,
    /** The most drivers one fleet read carries (the arrays are bound parameters), and the most link windows (a driver may have several). */
    maxFleetDrivers: 2_000,
    maxFleetWindows: 4_000,
    /** The most markets one fleet read may span. */
    maxFleetMarkets: 64,
    /** A driver's average rating is published only from this many ratings in the fixed trailing window (one rating would name the rider who gave it). */
    ratingFloor: 5,
    /** A fleet-level aggregate is published only when at least this many of its drivers had a trip in the period (fewer would be one driver's record). */
    aggregateFloor: 5,
};
/** The window used when the request names none (the account has no stored hours yet: owner question O-2 of the D534 handoff). */
export const DEFAULT_WORKING_WINDOW = Object.freeze({ fromHour: 7, toHour: 20, isoDays: Object.freeze([1, 2, 3, 4, 5]) });
export function validWorkingWindow(w) {
    return (Number.isInteger(w.fromHour) && Number.isInteger(w.toHour) && w.fromHour >= 0 && w.fromHour <= 23 && w.toHour >= 1 && w.toHour <= 24 && w.fromHour < w.toHour &&
        w.isoDays.length >= 1 && w.isoDays.length <= 7 && w.isoDays.every((d) => Number.isInteger(d) && d >= 1 && d <= 7) && new Set(w.isoDays).size === w.isoDays.length);
}
/** True when a local weekday (ISO) and hour fall outside the window. The SQL below computes the very same predicate; the tests pin them together. */
export const isOutOfHours = (isoDow, hour, w) => !(w.isoDays.includes(isoDow) && hour >= w.fromHour && hour < w.toHour);
/** The token for "this order's charge has no cost centre" in the `centers` filter and in the answer. */
export const NO_COST_CENTER = '';
const LOCAL = `(f.requested_at AT TIME ZONE $5::text)`;
const OOH = `(NOT (extract(isodow FROM ${LOCAL})::int = ANY($11::int[]) AND extract(hour FROM ${LOCAL})::int >= $9::int AND extract(hour FROM ${LOCAL})::int < $10::int))`;
/** The order's live charge postings and the cost centre they carry (the centre rows are the ACCOUNT's own: joined on its natural key). */
const SPEND_CHARGE = `LEFT JOIN LATERAL (
    SELECT sum(m.amount_minor) AS amount, min(m.currency::text) AS currency, min(pc.cost_center_code) AS cc, min(pc.cost_center_name) AS ccname
      FROM analytics.fact_money m
      LEFT JOIN analytics.dim_partner_account pc ON pc.market_id = m.market_id AND pc.partner_account_key = m.partner_account_key AND pc.natural_key = $2
     WHERE m.market_id = f.market_id AND m.order_id = f.order_id AND m.vertical = f.vertical
       AND m.source_kind IN (${PARTNER_CHARGE_KINDS})
       AND m.date_key BETWEEN f.date_key - 2 AND f.date_key + 31) pay ON true`;
/** `$1` markets, `$2` account, `$3` / `$4` local days, `$5` zone, `$6` kinds, `$7` operator keys, `$8` centres, `$9`-`$11` the working window, `$12` out-of-hours. */
const SPEND_CTE = `WITH o AS (
  SELECT f.market_id, f.vertical, f.order_id, f.order_code, f.status, f.outcome, f.category_code, f.requested_at, f.created_by_operator_key AS op,
         ${LOCAL} AS lt, ${OOH} AS ooh, pay.amount, pay.currency, COALESCE(pay.cc, '${NO_COST_CENTER}') AS cc, pay.ccname
    FROM analytics.fact_order f ${PARTNER_ACCOUNT_JOIN} ${SPEND_CHARGE}
   WHERE ${partnerRequestScope()} AND ($8::text[] IS NULL OR COALESCE(pay.cc, '${NO_COST_CENTER}') = ANY($8::text[]))
)`;
const OOH_FILTER = `($12::boolean IS NULL OR o.ooh = $12::boolean)`;
function spendParams(tenant, f) {
    if (!validWorkingWindow(f.window))
        throw new RangeError('the working window is invalid');
    const centers = nullable(f.centers);
    if (centers !== null && centers.some((c) => c !== NO_COST_CENTER && !CENTER_CODE.test(c)))
        throw new RangeError('a cost centre code is invalid');
    return [
        [...tenant.marketIds], tenant.accountRef, date(f.from), date(f.to), f.timeZone, nullable(f.kinds), nullable(f.operatorKeys), centers,
        f.window.fromHour, f.window.toHour, [...f.window.isoDays], f.outOfHours === undefined ? null : f.outOfHours,
    ];
}
/** The four aggregates behind the cards and the charts (same filters, same base). */
export function partnerSpendQueries(tenant, f) {
    const params = spendParams(tenant, f);
    const stmt = (select, group) => ({
        sql: `${SPEND_CTE}
SELECT ${select}, count(*)::int AS n, COALESCE(sum(o.amount), 0)::text AS amount FROM o WHERE ${OOH_FILTER} GROUP BY ${group}`,
        params,
    });
    return {
        markets: tenant.marketIds,
        byCenter: stmt('o.cc, max(o.ccname) AS ccname, o.vertical, o.currency', 'o.cc, o.vertical, o.currency'),
        byAgent: stmt('o.op, o.currency', 'o.op, o.currency'),
        byDay: stmt('(o.lt::date)::text AS day, o.vertical, o.currency', '1, o.vertical, o.currency'),
        byHour: stmt('extract(isodow FROM o.lt)::int AS dow, extract(hour FROM o.lt)::int AS hour, o.ooh, o.currency', '1, 2, 3, 4'),
    };
}
/** One page of the account's orders with their cost centre, newest first. */
export function partnerSpendRows(tenant, f, page) {
    const limit = clampInt(page.limit, 1, B2B_FLEET_LIMITS.exportRows);
    const offset = clampInt(page.offset, 0, B2B_FLEET_LIMITS.maxOffset);
    return {
        sql: `${SPEND_CTE}
SELECT o.requested_at, o.vertical, o.order_id, o.order_code, o.status, o.outcome, o.category_code, o.cc, o.ccname, o.op AS operator_key, o.amount::text AS amount, o.currency, o.ooh
  FROM o WHERE ${OOH_FILTER}
 ORDER BY o.requested_at DESC, o.order_id DESC
 LIMIT $13 OFFSET $14`,
        params: [...spendParams(tenant, f), limit, offset],
    };
}
/** The month's spend per cost centre and currency (the budgets' progress): no filter but the period, so a filter on the screen never shrinks a budget. */
export function partnerBudgetUsageQuery(tenant, month) {
    return {
        sql: `${SPEND_CTE}
SELECT o.cc, o.currency, count(*)::int AS n, COALESCE(sum(o.amount), 0)::text AS amount FROM o WHERE ${OOH_FILTER} GROUP BY 1, 2`,
        params: spendParams(tenant, { ...month, window: DEFAULT_WORKING_WINDOW }),
    };
}
export class FleetTenantError extends Error {
    code;
    constructor(code) {
        super(code);
        this.code = code;
        this.name = 'FleetTenantError';
    }
}
const COUNTRY = /^[a-z]{2}$/;
/** A fleet tenant. Call it with the organization the gate authorized and the windows the server derived - never with a value read from the request. */
export function fleetTenant(marketIds, fleetRef, windows) {
    if (!Array.isArray(marketIds) || marketIds.length === 0 || marketIds.length > B2B_FLEET_LIMITS.maxFleetMarkets || marketIds.some((m) => typeof m !== 'string' || !MARKET_ID.test(m))) {
        throw new FleetTenantError('FLEET_MARKETS_INVALID');
    }
    if (typeof fleetRef !== 'string' || !REF.test(fleetRef))
        throw new FleetTenantError('FLEET_REF_INVALID');
    if (windows.length > B2B_FLEET_LIMITS.maxFleetWindows)
        throw new FleetTenantError('FLEET_DRIVERS_TOO_MANY');
    const clean = [];
    for (const w of windows) {
        const since = Date.parse(w.since);
        const until = w.until === null ? Number.POSITIVE_INFINITY : Date.parse(w.until);
        if (typeof w.ref !== 'string' || !REF.test(w.ref) || !Number.isFinite(since) || Number.isNaN(until) || until <= since)
            throw new FleetTenantError('FLEET_DRIVERS_INVALID');
        if (w.market !== null && w.market !== undefined && !MARKET_ID.test(w.market))
            throw new FleetTenantError('FLEET_DRIVERS_INVALID');
        if (w.country !== null && w.country !== undefined && !COUNTRY.test(w.country))
            throw new FleetTenantError('FLEET_DRIVERS_INVALID');
        clean.push({ ref: w.ref, since: new Date(since).toISOString(), until: w.until === null ? null : new Date(until).toISOString(), market: w.market ?? null, country: w.country ?? null });
    }
    clean.sort((a, b) => (a.ref < b.ref ? -1 : a.ref > b.ref ? 1 : Date.parse(a.since) - Date.parse(b.since)));
    // two windows of one driver must not overlap: a trip would be counted twice
    for (let i = 1; i < clean.length; i += 1) {
        const prev = clean[i - 1];
        const cur = clean[i];
        if (prev.ref === cur.ref && (prev.until === null || Date.parse(prev.until) > Date.parse(cur.since)))
            throw new FleetTenantError('FLEET_DRIVERS_INVALID');
    }
    if (new Set(clean.map((w) => w.ref)).size > B2B_FLEET_LIMITS.maxFleetDrivers)
        throw new FleetTenantError('FLEET_DRIVERS_TOO_MANY');
    return Object.freeze({ kind: 'fleet', fleetRef, marketIds: Object.freeze([...new Set(marketIds)]), windows: Object.freeze(clean) });
}
/**
 * The fixed window a driver's RATING is averaged over: the last four whole ISO weeks (Monday to Sunday, UTC days) before the current week. The request cannot move
 * it: an average over a window the caller picks can be differenced (the same driver over two periods one day apart gives the rating of that day's riders).
 */
export function trailingRatingWindow(nowIso) {
    const now = new Date(nowIso);
    const day = Math.floor(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) / 86_400_000);
    const isoDow = ((day + 3) % 7) + 1; // 1970-01-01 was a Thursday
    const thisMonday = day - (isoDow - 1);
    const iso = (n) => new Date(n * 86_400_000).toISOString().slice(0, 10);
    return { from: iso(thisMonday - 28), to: iso(thisMonday - 1) };
}
/**
 * `$1` markets, `$2`-`$6` the link windows (driver ref, since, until - 'infinity' while it runs -, the market and the country the grant is limited to), `$7` / `$8`
 * local days, `$9` kinds, `$10` driver filter. The cohort is joined on `natural_key` in BOTH person dimensions (a fleet's driver may drive rides, a courier may
 * carry Sends); every fact joins the cohort's surrogate keys and the window the fact falls in (`requested_at >= since AND < until`), so what a driver did before
 * the link, after it ended, or in a gap between two links is not the fleet's to read.
 */
const FLEET_CTE = `WITH g AS (
  SELECT u.ref, u.since, u.until, u.mkt, u.cc
    FROM unnest($2::text[], $3::timestamptz[], $4::timestamptz[], $5::text[], $6::text[]) AS u(ref, since, until, mkt, cc)
   WHERE $10::text[] IS NULL OR u.ref = ANY($10::text[])
), fd AS (
  SELECT d.market_id, d.driver_key AS person_key, g.ref, g.since, g.until
    FROM g JOIN analytics.dim_driver d ON d.natural_key = g.ref AND d.market_id = ANY($1::text[])
     AND (g.mkt IS NULL OR d.market_id = g.mkt) AND (g.cc IS NULL OR d.market_id LIKE g.cc || ':%')
), fc AS (
  SELECT c.market_id, c.courier_key AS person_key, g.ref, g.since, g.until
    FROM g JOIN analytics.dim_courier c ON c.natural_key = g.ref AND c.market_id = ANY($1::text[])
     AND (g.mkt IS NULL OR c.market_id = g.mkt) AND (g.cc IS NULL OR c.market_id LIKE g.cc || ':%')
), t AS (
  SELECT fd.ref, f.market_id, f.vertical, f.order_id, f.order_code, f.status, f.outcome, f.cancel_actor, f.requested_at, f.date_key, f.timezone, f.distance_meters, f.transit_seconds
    FROM analytics.fact_order f JOIN fd ON fd.market_id = f.market_id AND fd.person_key = f.driver_key
   WHERE f.vertical = 'RIDE' AND f.market_id = ANY($1::text[]) AND f.date_key BETWEEN $7::date AND $8::date AND f.requested_at >= fd.since AND f.requested_at < fd.until
     AND ($9::text[] IS NULL OR f.vertical = ANY($9::text[]))
  UNION ALL
  SELECT fc.ref, f.market_id, f.vertical, f.order_id, f.order_code, f.status, f.outcome, f.cancel_actor, f.requested_at, f.date_key, f.timezone, f.distance_meters, f.transit_seconds
    FROM analytics.fact_order f JOIN fc ON fc.market_id = f.market_id AND fc.person_key = f.courier_key
   WHERE f.vertical = 'DELIVERY' AND f.market_id = ANY($1::text[]) AND f.date_key BETWEEN $7::date AND $8::date AND f.requested_at >= fc.since AND f.requested_at < fc.until
     AND ($9::text[] IS NULL OR f.vertical = ANY($9::text[]))
)`;
const DONE = `t.outcome = '${V.outcome.completed}'`;
const CANCELLED = `(t.status = '${V.outcome.cancelled}' OR t.outcome = '${V.outcome.cancelled}')`;
function fleetParams(tenant, f) {
    const w = tenant.windows;
    return [
        [...tenant.marketIds], w.map((x) => x.ref), w.map((x) => x.since), w.map((x) => x.until ?? 'infinity'), w.map((x) => x.market ?? null), w.map((x) => x.country ?? null),
        date(f.from), date(f.to), nullable(f.kinds), nullable(f.drivers),
    ];
}
/** The aggregates of the driver table, the fleet cards and the day chart. No money, no person beyond the fleet's own driver ref. */
export function fleetQueries(tenant, f) {
    const params = fleetParams(tenant, f);
    return {
        markets: tenant.marketIds,
        trips: {
            sql: `${FLEET_CTE}
SELECT t.ref AS driver_ref, t.vertical,
       count(*)::int AS trips,
       (count(*) FILTER (WHERE ${DONE}))::int AS completed,
       (count(*) FILTER (WHERE ${CANCELLED}))::int AS cancelled,
       (count(*) FILTER (WHERE ${CANCELLED} AND t.cancel_actor = '${V.cancelActor.driver}'))::int AS cancelled_by_driver,
       COALESCE(sum(t.distance_meters) FILTER (WHERE ${DONE}), 0)::text AS distance_meters,
       COALESCE(sum(t.transit_seconds) FILTER (WHERE ${DONE}), 0)::text AS trip_seconds
  FROM t GROUP BY 1, 2`,
            params,
        },
        // offers exist for rides only (a courier's offers are not in the store): acceptance of a courier is "not recorded", never a zero
        offers: {
            sql: `${FLEET_CTE}
SELECT fd.ref AS driver_ref,
       (count(*) FILTER (WHERE o.status = '${V.offerStatus.accepted}'))::int AS accepted,
       (count(*) FILTER (WHERE o.status = '${V.offerStatus.rejected}'))::int AS rejected,
       (count(*) FILTER (WHERE o.status = '${V.offerStatus.expired}'))::int AS expired
  FROM analytics.fact_offer o JOIN fd ON fd.market_id = o.market_id AND fd.person_key = o.driver_key
 WHERE o.market_id = ANY($1::text[]) AND o.date_key BETWEEN $7::date AND $8::date AND o.created_at >= fd.since AND o.created_at < fd.until AND ($9::text[] IS NULL OR 'RIDE' = ANY($9::text[]))
 GROUP BY 1`,
            params,
        },
        days: {
            sql: `${FLEET_CTE}
SELECT t.date_key::text AS day, t.vertical,
       count(*)::int AS trips, (count(*) FILTER (WHERE ${DONE}))::int AS completed, (count(*) FILTER (WHERE ${CANCELLED}))::int AS cancelled,
       COALESCE(sum(t.distance_meters) FILTER (WHERE ${DONE}), 0)::text AS distance_meters
  FROM t GROUP BY 1, 2`,
            params,
        },
    };
}
/**
 * The riders' ratings of the fleet's drivers over {@link trailingRatingWindow} - a window the REQUEST cannot choose (P2 of the D534 review) - and only inside each
 * driver's link windows. The period, the kinds and the driver filter of the screen never reach it, so two views one day apart cannot be differenced into one rating.
 */
export function fleetRatingQuery(tenant, window) {
    return {
        sql: `${FLEET_CTE}
SELECT fd.ref AS driver_ref, count(*)::int AS n, COALESCE(sum(r.stars), 0)::int AS stars_sum
  FROM analytics.fact_rating r JOIN fd ON fd.market_id = r.market_id AND fd.person_key = r.driver_key
 WHERE r.market_id = ANY($1::text[]) AND r.rater_role = 'RIDER' AND r.subject_kind = 'DRIVER' AND r.date_key BETWEEN $7::date AND $8::date AND r.rated_at >= fd.since AND r.rated_at < fd.until
 GROUP BY 1`,
        params: fleetParams(tenant, { from: window.from, to: window.to }),
    };
}
/** One page of the fleet's trips, newest first. */
export function fleetTripRows(tenant, f, page) {
    const limit = clampInt(page.limit, 1, B2B_FLEET_LIMITS.exportRows);
    const offset = clampInt(page.offset, 0, B2B_FLEET_LIMITS.maxOffset);
    return {
        sql: `${FLEET_CTE}
SELECT t.market_id, t.requested_at, t.timezone, t.vertical, t.order_id, t.order_code, t.status, t.outcome, t.cancel_actor, t.ref AS driver_ref, t.distance_meters, t.transit_seconds
  FROM t ORDER BY t.requested_at DESC, t.order_id DESC LIMIT $11 OFFSET $12`,
        params: [...fleetParams(tenant, f), limit, offset],
    };
}
//# sourceMappingURL=b2b-fleet.js.map